import SleepDiaryChart, {
  DEFAULT_SLEEP_BAR_ATTR,
  LegendItem,
  PlotType,
} from "@/components/report/commons/SleepDiaryChart";
import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { max, min } from "date-fns";
import { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  EachDayResultType,
  EmphasizedChartData,
  ImpactScore,
  LifeStyleType,
  RecommendedSleepTime,
  SolutionType,
} from ".";
import {
  ImpactScoreResultImg,
  ReportDailyResultImg,
  ThreeLevelScale,
  ThreeLevelScalesType,
} from "./commons";
import YourImprovementMethodButton from "./HelpDialog/YourImprovementMethodButton";
import { ImpactScoreTable } from "./YourImprovementMethod";

export interface ImprovementActionProps {
  recommended_to_visit_medical_institution: boolean;
  summary_lines: string[] | null;
  description_lines: string[] | null;
  learning: {
    caption: string;
    type: SolutionType;
  } | null;
  emphasized_chart_data: EmphasizedChartData | null;
  sleep_quality_impact_scores: ImpactScore[];
  each_day_result: Record<EachDayResultType, (ThreeLevelScalesType | null)[]>;
  wakeup_time_gap: (string | null)[];
  sleep_time: (string | null)[];
  recommended_sleep_time: RecommendedSleepTime;
}

export default function ImprovementAction({
  recommended_to_visit_medical_institution,
  summary_lines,
  description_lines,
  learning,
  emphasized_chart_data,
  sleep_quality_impact_scores,
  each_day_result,
  wakeup_time_gap,
  sleep_time,
  recommended_sleep_time,
}: ImprovementActionProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const diseaseRiskReportPath = `${pathname}${pathname.endsWith("/") ? "" : "/"}disease_risk`;
  return (
    <>
      <Stack sx={{ mx: 6, gap: 4, mb: 12 }}>
        <Stack spacing={2}>
          <Typography fontWeight="500" fontSize="18px" lineHeight="32.4px">
            あなたの改善アクション
          </Typography>
          <Divider />
        </Stack>
        {summary_lines != null && (
          <Stack
            sx={{
              background: "linear-gradient(270deg, #017453 0%, #01A676 100%)",
              borderRadius: "8px",
              alignItems: "center",
              p: 5,
            }}
          >
            {summary_lines.length === 1 && (
              <Typography
                variant="h6"
                fontWeight={500}
                fontSize="20px"
                lineHeight="34px"
                color="#FFFFFF"
                textAlign="center"
              >
                {summary_lines[0]}
              </Typography>
            )}
            {summary_lines.length > 1 && (
              <>
                <Typography
                  variant="h6"
                  fontWeight={500}
                  fontSize="14px"
                  lineHeight="22.4px"
                  color="#FFFFFF"
                  textAlign="center"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {summary_lines.slice(0, -1).join("\n")}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={500}
                  fontSize="20px"
                  lineHeight="34px"
                  color="#FFFFFF"
                  textAlign="center"
                >
                  {summary_lines.at(-1)}
                </Typography>
              </>
            )}
          </Stack>
        )}
        {description_lines?.map((d, i) => (
          <Typography
            key={i}
            variant="body1"
            sx={{
              color: "text.primary",
              textAlign: "justify",
              whiteSpace: "pre-wrap",
            }}
          >
            {d}
          </Typography>
        ))}
        {recommended_to_visit_medical_institution && (
          <Stack gap={4} sx={{ mt: 8 }}>
            <Button
              variant="contained"
              sx={{
                color: "primary.contrast",
                fontSize: "15px",
                fontWeight: "500",
                p: 2,
              }}
              onClick={() => {
                window.open(diseaseRiskReportPath, "_blank");
              }}
              disableElevation
            >
              医療機関向けレポートを表示
            </Button>
            <Button
              variant="outlined"
              sx={{
                color: "primary.main",
                fontSize: "15px",
                fontWeight: "500",
                p: 2,
              }}
              component={RouterLink}
              to="search_hospital"
              disableElevation
            >
              医療機関を探す
            </Button>
          </Stack>
        )}
        {!recommended_to_visit_medical_institution && (
          <>
            {learning != null && (
              <Box textAlign="right">
                <YourImprovementMethodButton
                  solution={{ caption: learning.caption, type: learning.type }}
                />
              </Box>
            )}
            <Divider />
            <Stack gap={4}>
              {emphasized_chart_data?.type === "length" && (
                <Length
                  recommended_sleep_time={recommended_sleep_time}
                  sleep_time={sleep_time}
                  sleep_length={each_day_result.sleep_length}
                  chart_data={emphasized_chart_data.chart_data}
                />
              )}
              {emphasized_chart_data?.type === "quality" && (
                <Quality
                  each_day_result={each_day_result}
                  chart_data={emphasized_chart_data.chart_data}
                  impact_scores={sleep_quality_impact_scores}
                />
              )}
              {emphasized_chart_data?.type === "rhythm" && (
                <Rhythm
                  wakeup_time_gap={wakeup_time_gap}
                  emphasized_chart_data={emphasized_chart_data}
                  sleep_rhythm={each_day_result.sleep_rhythm}
                />
              )}
            </Stack>
          </>
        )}
      </Stack>
      <Divider />
    </>
  );
}

function Length({
  sleep_time,
  sleep_length,
  chart_data,
  recommended_sleep_time,
}: {
  sleep_time: (string | null)[];
  sleep_length: (ThreeLevelScalesType | null)[];
  chart_data: {
    date: string;
    arise_time: string;
    sleep_data: {
      from: string;
      to: string;
      type: PlotType;
    }[];
  }[];
  recommended_sleep_time: RecommendedSleepTime;
}) {
  return (
    <>
      <Typography variant="subtitle1">
        あなたの睡眠時間の記録と日別の判定
      </Typography>
      <Stack sx={{ width: "100%", overflowX: "scroll" }}>
        <TableContainer
          sx={{
            minWidth: "752px",
            borderRadius: 0,
            border: "solid 1px #D8D7D6",
            "& thead tr th, tr tbody td": {
              whiteSpace: "nowrap",
            },
            "& tbody tr td": {
              py: 4,
              px: 0,
              border: "none",
              "&:nth-of-type(1)": {
                px: 4,
                borderRight: "solid 1px #D8D7D6",
              },
              "&:not(:nth-of-type(-n + 1))": {
                textAlign: "center",
              },
            },
            "& tbody tr:nth-of-type(1) td": {
              borderBottom: "3px double #D8D7D6",
            },
            "& thead tr th": {
              p: "8px 0",
              "&:not(:nth-of-type(1))": {
                width: "83.71px",
                "& .MuiTypography-caption": {
                  display: "block",
                  textAlign: "center",
                },
              },
              "&:nth-of-type(1)": {
                width: "120px",
                borderRight: "solid 1px #D8D7D6",
                px: "16px",
                "& .MuiTypography-caption": {
                  display: "block",
                  textAlign: "left",
                },
              },
              "&:not(:nth-of-type(-n + 2))": {
                "& .MuiTypography-caption": {
                  borderLeft: "solid 1px #D8D7D6",
                },
              },
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="caption">項目</Typography>
                </TableCell>
                {Array.from({ length: 7 }).map((_, i) => (
                  <TableCell key={i}>
                    <Typography variant="caption">{i + 1}日目</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography fontWeight={400} fontSize="13px">
                    睡眠時間
                  </Typography>
                </TableCell>
                {sleep_time.map((x, i) => (
                  <TableCell key={i} sx={{ textAlign: "center" }}>
                    {x != null ? (
                      <Typography fontWeight={400} fontSize="13px">
                        {x}
                      </Typography>
                    ) : (
                      <Typography fontWeight={400} fontSize="13px">
                        -
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography fontWeight={400} fontSize="13px">
                    日別判定
                  </Typography>
                </TableCell>
                {sleep_length.map((x, i) => (
                  <TableCell key={i} sx={{ textAlign: "center" }}>
                    {x != null ? (
                      <ReportDailyResultImg
                        type={x}
                        style={{ margin: "auto", display: "block" }}
                      />
                    ) : (
                      <Typography fontWeight={400} fontSize="13px">
                        -
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        ※推奨睡眠時間（{recommended_sleep_time.age_range}）：
        {recommended_sleep_time.recommended_sleep_time_low}-
        {recommended_sleep_time.recommended_sleep_time_high}時間
      </Typography>
      <Divider
        sx={{
          border: "none",
          borderTop: "2px dotted #E0E0E0",
          mt: 12,
          mb: 6.5,
        }}
      />
      <LengthChart chart_data={chart_data} sleep_length={sleep_length} />
    </>
  );
}

function LengthChart({
  chart_data,
  sleep_length,
}: {
  chart_data: {
    date: string;
    arise_time: string;
    sleep_data: {
      from: string;
      to: string;
      type: PlotType;
    }[];
  }[];
  sleep_length: (ThreeLevelScalesType | null)[];
}) {
  return (
    <Stack gap={4}>
      <Stack>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "500", fontSize: "16px" }}
        >
          デバイス測定データ┃睡眠の長さ
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="end">
        <LegendItem
          imageName="image_report_how_to_view_graphs_sleep.svg"
          text="睡眠"
        />
      </Stack>
      <Stack sx={{ height: "311px" }}>
        <SleepDiaryChart
          data={chart_data.map((d, i) => ({
            date: d.date,
            bedtime: null,
            ariseTime: null,
            sleepData:
              d.sleep_data.length > 0
                ? [
                    {
                      from: new Date(d.sleep_data.at(0)!.from),
                      to: new Date(d.sleep_data.at(-1)!.to),
                      type: "sleep",
                    },
                  ]
                : [],
            dateDecoration: (
              [ThreeLevelScale.BAD, ThreeLevelScale.NORMAL] as number[]
            ).includes(sleep_length[i] ?? 0)
              ? {
                  fill: "#FF3A30",
                  weight: 700,
                }
              : null,
          }))}
        />
      </Stack>
    </Stack>
  );
}

function Quality({
  chart_data,
  each_day_result,
  impact_scores,
}: {
  chart_data: {
    date: string;
    arise_time: string;
    sleep_data: {
      from: string;
      to: string;
      type: PlotType;
    }[];
  }[];
  each_day_result: Record<EachDayResultType, (ThreeLevelScalesType | null)[]>;
  impact_scores: {
    value: number | null;
    name: LifeStyleType;
    caption: string;
  }[];
}) {
  return (
    <>
      <QualityChart
        chart_data={chart_data}
        sleep_quality={each_day_result.sleep_quality}
      />
      <Divider
        sx={{
          border: "none",
          borderTop: "2px dotted #E0E0E0",
          mt: 12,
          mb: 6.5,
        }}
      />
      <Stack gap={2}>
        <Typography variant="subtitle1">
          睡眠の質に悪影響を与えた生活習慣の記録
        </Typography>
        {impact_scores.some((d) => (d.value ?? 0) >= 55) ? (
          <BadEffects
            each_day_result={each_day_result}
            impact_scores={impact_scores}
          />
        ) : (
          <NoEffects
            each_day_result={each_day_result}
            impact_scores={impact_scores}
          ></NoEffects>
        )}
      </Stack>
      <Divider />
    </>
  );
}

function NoEffects({
  each_day_result,
  impact_scores,
}: {
  each_day_result: Record<EachDayResultType, (ThreeLevelScalesType | null)[]>;
  impact_scores: {
    value: number | null;
    name: LifeStyleType;
    caption: string;
  }[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <Stack gap={4}>
      <Stack sx={{ backgroundColor: "#FF3A3014", px: 3, py: 4 }}>
        <Typography variant="body2" color="#FF3A30">
          今回の測定で睡眠の質に悪影響を強く与えていそうな生活習慣はありませんでした。
        </Typography>
      </Stack>
      {open && (
        <Stack sx={{ width: "100%", overflowX: "scroll" }}>
          <TableContainer
            sx={{
              minWidth: "752px",
              borderRadius: 0,
              border: "solid 1px #D8D7D6",
              "& thead tr th, tr tbody td": {
                whiteSpace: "nowrap",
              },
              "& thead tr th": {
                "&:nth-of-type(1), &:nth-of-type(2)": {
                  px: 4,
                  borderRight: "solid 1px #D8D7D6",
                },
              },
              "& tbody tr": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#FAFAFA",
                },
                "& td": {
                  "&:nth-of-type(1)": {
                    textAlign: "center",
                  },
                  "&:nth-of-type(1), &:nth-of-type(2)": {
                    px: 4,
                    borderRight: "solid 1px #D8D7D6",
                  },
                },
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="caption">影響スコア</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">項目</Typography>
                  </TableCell>
                  {Array.from({ length: 7 }).map((_, i) => (
                    <TableCell key={i}>
                      <Typography variant="caption">{i + 1}日目</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {impact_scores.map((x, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Typography fontWeight={400} fontSize="13px">
                        {x.value ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={400} fontSize="13px">
                        {x.caption}
                      </Typography>
                    </TableCell>
                    {each_day_result[x.name]?.map((s, j) => (
                      <TableCell key={j} sx={{ textAlign: "center" }}>
                        <ImpactScoreResultImg
                          key={j}
                          type={s}
                          style={{ margin: "auto", display: "block" }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
      {!open && (
        <Box>
          <Button
            variant="text"
            sx={{ color: "text.secondary", p: 0 }}
            endIcon={<KeyboardArrowDown />}
            onClick={() => setOpen(true)}
          >
            生活習慣の一覧を見る
          </Button>
        </Box>
      )}
    </Stack>
  );
}

function BadEffects({
  each_day_result,
  impact_scores,
}: {
  each_day_result: Record<EachDayResultType, (ThreeLevelScalesType | null)[]>;
  impact_scores: {
    value: number | null;
    name: LifeStyleType;
    caption: string;
  }[];
}) {
  const [open, setOpen] = useState(false);

  const impactScores = impact_scores.map((x) => ({
    ...x,
    each_day_result: each_day_result[x.name],
  }));
  if (!open) {
    impactScores.splice(1);
  }

  return (
    <Stack gap={4}>
      <Typography variant="caption" color="text.secondary" align="justify">
        ※影響スコア：睡眠の質に悪影響を与えた可能性を評価し、スコアが55以上で悪影響あり、100に近いほど悪影響が強いことを示します。
      </Typography>
      <Stack alignItems="end">
        <Stack direction="row" gap={1.5}>
          <ImpactScoreResultImg type={1} />
          <Typography
            fontWeight={400}
            fontSize="12px"
            lineHeight="175%"
            textAlign="justify"
          >
            生活習慣に課題があった日
          </Typography>
        </Stack>
      </Stack>
      <Stack>
        <ImpactScoreTable impact_scores={impactScores} />
        {!open && (
          <Box>
            <Button
              variant="text"
              sx={{ color: "text.secondary", fontSize: "13px" }}
              endIcon={<KeyboardArrowDown />}
              onClick={() => setOpen(true)}
            >
              影響スコア2位以下を表示
            </Button>
          </Box>
        )}
      </Stack>
    </Stack>
  );
}

function QualityChart({
  sleep_quality,
  chart_data,
}: {
  sleep_quality: (ThreeLevelScalesType | null)[];
  chart_data: {
    date: string;
    arise_time: string;
    sleep_data: {
      from: string;
      to: string;
      type: PlotType;
    }[];
  }[];
}) {
  return (
    <Stack gap={4}>
      <Stack>
        <Typography variant="subtitle1">デバイス測定データ┃睡眠の質</Typography>
      </Stack>
      <Stack direction="row" justifyContent="end">
        <LegendItem
          imageName="image_report_how_to_view_graphs_mid-awakening.svg"
          text="寝床にいる間の覚醒"
        />
      </Stack>
      <Stack sx={{ height: "311px" }}>
        <SleepDiaryChart
          sleepBarAttributes={{
            ...DEFAULT_SLEEP_BAR_ATTR,
            active: {
              sleep: { fill: "#E0E0E0" },
              awake: { fill: "#FF3A30" },
              no_data: { fill: "#FF3A30" },
              nap: { fill: "#E0E0E0" },
            },
          }}
          data={chart_data.map((d, i) => ({
            date: d.date,
            bedtime: null,
            ariseTime: null,
            sleepData: d.sleep_data.map((d) => ({
              from: new Date(d.from),
              to: new Date(d.to),
              type: d.type,
            })),
            dateDecoration: (
              [ThreeLevelScale.BAD, ThreeLevelScale.NORMAL] as number[]
            ).includes(sleep_quality[i] ?? 0)
              ? {
                  fill: "#FF3A30",
                  weight: 700,
                }
              : null,
          }))}
        />
      </Stack>
      <Stack>
        <Divider />
        <Stack direction="row" sx={{ py: 2 }}>
          <Typography
            sx={{
              color: "#707070",
              fontSize: "11px",
              fontWeight: "700",
              whiteSpace: "nowrap",
            }}
          >
            判定
          </Typography>
          <Stack direction="row" sx={{ width: "100%" }}>
            {sleep_quality.map((x, i) => (
              <div
                key={i}
                style={{
                  width: "calc(100% / 7)",
                  textAlign: "center",
                  verticalAlign: "middle",
                }}
              >
                {x != null ? (
                  <ReportDailyResultImg
                    key={i}
                    type={x}
                    style={{
                      margin: "auto",
                    }}
                  />
                ) : (
                  <Typography fontWeight={400} fontSize="13px">
                    -
                  </Typography>
                )}
              </div>
            ))}
          </Stack>
        </Stack>
        <Divider />
      </Stack>
    </Stack>
  );
}

function Rhythm({
  sleep_rhythm,
  emphasized_chart_data,
  wakeup_time_gap,
}: {
  sleep_rhythm: (ThreeLevelScalesType | null)[];
  wakeup_time_gap: (string | null)[];
  emphasized_chart_data: {
    type: "length" | "quality" | "rhythm";
    average_wake_up_time: string | null;
    chart_data: {
      date: string;
      arise_time: string;
      sleep_data: {
        from: string;
        to: string;
        type: PlotType;
      }[];
    }[];
  };
}) {
  return (
    <>
      <Typography variant="subtitle1">
        睡眠のリズムの指標と日別の判定
      </Typography>
      <Stack>
        <table>
          <tbody>
            <tr>
              <td style={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2">
                  あなたの平均的な起床時刻
                </Typography>
              </td>
              <td width="100%">
                <Divider
                  sx={{
                    mx: 2,
                    border: "none",
                    borderTop: "2px dashed #E0E0E0",
                    "&:before": {
                      content: "''",
                      position: "absolute",
                      left: 0,
                      top: "50%",
                      width: "100%",
                      height: "1px",
                    },
                  }}
                />
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "text.primary",
                  }}
                >
                  {emphasized_chart_data?.average_wake_up_time ?? "-"}
                </Typography>
              </td>
            </tr>
          </tbody>
        </table>
      </Stack>
      <Stack sx={{ width: "100%", overflowX: "scroll" }}>
        <TableContainer
          sx={{
            minWidth: "752px",
            borderRadius: 0,
            border: "solid 1px #D8D7D6",
            "& thead tr th, tr tbody td": {
              whiteSpace: "nowrap",
            },
            "& tbody tr:nth-of-type(1) td": {
              borderBottom: "3px double #D8D7D6",
            },
            "& thead tr th": {
              p: "8px 0",
              "&:nth-of-type(1)": {
                px: 4,
                borderRight: "solid 1px #D8D7D6",
                width: "120px",
                "& .MuiTypography-caption": {
                  display: "block",
                  textAlign: "left",
                },
              },
              "&:not(:nth-of-type(1))": {
                width: "83.71px",
                "& .MuiTypography-caption": {
                  display: "block",
                  textAlign: "center",
                },
              },
              "&:not(:nth-of-type(-n + 2))": {
                "& .MuiTypography-caption": {
                  borderLeft: "solid 1px #D8D7D6",
                },
              },
            },
            "& tbody tr": {
              "& td": {
                "&:nth-of-type(1)": {
                  textAlign: "center",
                  px: 4,
                  borderRight: "solid 1px #D8D7D6",
                },
              },
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="caption">項目</Typography>
                </TableCell>
                {Array.from({ length: 7 }).map((_, i) => (
                  <TableCell key={i}>
                    <Typography variant="caption">{i + 1}日目</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography
                    fontWeight={400}
                    fontSize="13px"
                    sx={{ textAlign: "left" }}
                  >
                    起床時刻のズレ
                  </Typography>
                </TableCell>
                {wakeup_time_gap.map((x, i) => (
                  <TableCell key={i} sx={{ textAlign: "center" }}>
                    {x != null ? (
                      <Typography variant="caption">{x}</Typography>
                    ) : (
                      <Typography fontWeight={400} fontSize="13px">
                        -
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography
                    fontWeight={400}
                    fontSize="13px"
                    sx={{ textAlign: "left" }}
                  >
                    日別判定
                  </Typography>
                </TableCell>
                {sleep_rhythm.map((x, i) => (
                  <TableCell key={i} sx={{ textAlign: "center" }}>
                    {x != null ? (
                      <ReportDailyResultImg
                        type={x}
                        style={{ margin: "auto", display: "block" }}
                      />
                    ) : (
                      <Typography fontWeight={400} fontSize="13px">
                        -
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      <Divider
        sx={{
          border: "none",
          borderTop: "2px dotted #E0E0E0",
          mt: 12,
          mb: 6.5,
        }}
      />
      <RhythmChart
        emphasized_chart_data={emphasized_chart_data}
        sleep_rhythm={sleep_rhythm}
      />
      <Divider />
    </>
  );
}

function RhythmChart({
  emphasized_chart_data,
  sleep_rhythm,
}: {
  emphasized_chart_data: {
    type: "length" | "quality" | "rhythm";
    average_wake_up_time: string | null;
    chart_data: {
      date: string;
      arise_time: string;
      sleep_data: {
        from: string;
        to: string;
        type: PlotType;
      }[];
    }[];
  };
  sleep_rhythm: (ThreeLevelScalesType | null)[];
}) {
  return (
    <Stack gap={4}>
      <Stack>
        <Typography variant="subtitle1">
          デバイス測定データ┃睡眠のリズム
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="end" gap={4}>
        <LegendItem
          imageName="image_report_how_to_view_graphs_in-bed.svg"
          text="寝床にいた時間"
        />
        <LegendItem imageName="image_top_wake_up.svg" text="起床" />
        <LegendItem
          imageName="image_report_how_to_view_graphs_wakeup.svg"
          text="平均的な起床時刻"
        />
      </Stack>

      <Stack sx={{ height: "311px" }}>
        <SleepDiaryChart
          wakeupTime={{
            value: emphasized_chart_data.average_wake_up_time,
            range: 2,
            attribute: {
              line: {
                fill: "#ffffff",
                "stroke-width": 1,
                stroke: "#00A99D",
              },
              area: { fill: "#00A99D", "fill-opacity": "0.12" },
            },
          }}
          sleepBarAttributes={{
            ...DEFAULT_SLEEP_BAR_ATTR,
            active: {
              sleep: { fill: "#feebcc" },
              awake: { fill: "#feebcc" },
              no_data: { fill: "#feebcc" },
              nap: { fill: "#feebcc" },
            },
          }}
          data={emphasized_chart_data.chart_data.map((d, i) => ({
            date: d.date,
            bedtime: null,
            ariseTime: new Date(d.arise_time),
            sleepData:
              d.sleep_data.length > 0
                ? [
                    d.sleep_data
                      .map((d) => ({
                        from: new Date(d.from),
                        to: new Date(d.to),
                        type: d.type,
                      }))
                      .reduce((a, c) => ({
                        from: min([a.from, c.from]), // 最小の from を設定
                        to: max([a.to, c.to]), // 最大の to を設定
                        type: "sleep",
                      })),
                  ]
                : [],
            dateDecoration: (
              [ThreeLevelScale.BAD, ThreeLevelScale.NORMAL] as number[]
            ).includes(sleep_rhythm[i] ?? 0)
              ? {
                  fill: "#FF3A30",
                  weight: 700,
                }
              : null,
          }))}
        />
      </Stack>
    </Stack>
  );
}
